<template>
  <!-- Skip to main content for keyboard users -->
  <a href="#main-content" class="visually-hidden skip-link">Skip to main content</a>

  <div id="main-content" class="profile-edit-router" role="main">
    <!-- Loading state -->
    <div v-if="loading" class="loading-container" role="status" aria-live="polite">
      <div class="spinner" aria-hidden="true"></div>
      <p>Loading profile...</p>
    </div>

    <!-- Route to appropriate profile edit component based on role -->
    <ArtistProfileEdit v-else-if="userRole === 'artist'" />
    <ScientistProfileEdit v-else-if="userRole === 'scientist'" />

    <!-- Users with 'both' role: default to Artist profile (can switch via ?type=scientist) -->
    <ArtistProfileEdit v-else-if="userRole === 'both'" />

    <!-- Error state for invalid or missing role -->
    <div v-else class="error-container" role="alert">
      <h2>Unable to Load Profile Editor</h2>
      <p>Please ensure your account has a valid role assigned.</p>
      <button @click="goToDashboard" class="back-btn" aria-label="Return to your dashboard">Return to Dashboard</button>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { doc, getDoc } from 'firebase/firestore'
import { onAuthStateChanged } from 'firebase/auth'
import { auth, db } from '../firebase'
import ArtistProfileEdit from './ArtistProfileEdit.vue'
import ScientistProfileEdit from './ScientistProfileEdit.vue'

export default {
  name: 'ProfileEditPage',
  components: {
    ArtistProfileEdit,
    ScientistProfileEdit
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    const loading = ref(true)
    const userRole = ref(null)
    const user = ref(null)

    const loadUserRole = async () => {
      if (!user.value) {
        loading.value = false
        return
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', user.value.uid))
        if (userDoc.exists()) {
          const userData = userDoc.data()
          // Check for type query parameter to override role
          const requestedType = route.query.type
          if (requestedType && (requestedType === 'artist' || requestedType === 'scientist')) {
            userRole.value = requestedType
          } else {
            // Use role from user document
            userRole.value = userData.role || userData.profileRole || userData.selectedRole
          }
        } else {
          console.error('User document not found')
        }
      } catch (error) {
        console.error('Error loading user role:', error)
      } finally {
        loading.value = false
      }
    }

    const goToDashboard = () => {
      router.push('/dashboard')
    }

    onMounted(() => {
      onAuthStateChanged(auth, (currentUser) => {
        user.value = currentUser
        if (currentUser) {
          loadUserRole()
        } else {
          loading.value = false
          router.push('/HomePage')
        }
      })
    })

    return {
      loading,
      userRole,
      goToDashboard
    }
  }
}
</script>

<style scoped>
/* ===== ACCESSIBILITY STYLES ===== */
.visually-hidden {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

.skip-link {
  position: absolute;
  top: -40px;
  left: 6px;
  background: #6566be;
  color: white;
  padding: 8px;
  text-decoration: none;
  border-radius: 4px;
  z-index: 10000;
  font-weight: 600;
  transition: top 0.2s ease;
}

.skip-link:focus {
  top: 6px;
}

/* Enhanced focus indicators for keyboard users */
.back-btn:focus:not(:focus-visible) {
  outline: none;
}

.back-btn:focus-visible {
  outline: 2px solid #6566be;
  outline-offset: 2px;
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .back-btn {
    border-width: 3px;
  }

  .spinner {
    border-width: 4px;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .spinner {
    animation: none;
  }

  .back-btn {
    transition: none;
  }
}

/* Touch target size for mobile */
@media (max-width: 768px) {
  .back-btn {
    min-height: 44px;
    min-width: 44px;
    padding: 12px 24px;
  }
}

.profile-edit-router {
  min-height: 100vh;
  width: 100%;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  gap: 1rem;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #d1d2e6;
  border-top: 3px solid #6566be;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  will-change: transform;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
  .spinner {
    animation: none;
  }
}

.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  gap: 1rem;
  text-align: center;
  padding: 2rem;
}

.error-container h2 {
  color: #333;
  margin-bottom: 0.5rem;
}

.error-container p {
  color: #666;
  margin-bottom: 1rem;
}

.back-btn {
  background: white;
  color: #6566be;
  border: 2px solid #6566be;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.back-btn:hover {
  background: #6566be;
  color: white;
}

</style>